import { Checkbox, Col, Form, Input, Row, message } from "antd"
import { CheckboxChangeEvent } from "antd/es/checkbox"
import { FC, useState } from "react"
import { IMaskInput } from "react-imask"
import { Link } from "react-router-dom"

import Button from "@/components/button/Button"
import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import WebsiteTypography from "@/components/websiteTypography/WebsiteTypography"
import { usePricingSubmitRequestMutation } from "@/graphql"
import { websiteRoutes } from "@/router/routes"

const { Title, Paragraph, Text } = WebsiteTypography

type PricingFormValues = {
  email: string
  name: string
  companyName: string
  mobilephone: string
  consent: boolean
}

const PricingRequest: FC = () => {
  const [form] = Form.useForm<PricingFormValues>()
  const { website } = websiteRoutes
  const [pricingSubmitRequest, { loading }] = usePricingSubmitRequestMutation()
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true)

  const { setModalDarkTypes } = useModalDarkContext()

  const handleCheckboxChange = (event: CheckboxChangeEvent) => {
    setIsSubmitDisabled(!event.target.checked)
  }

  const handleSubmitClick = () => {
    form.submit()
  }

  const onFinish = async (values: PricingFormValues) => {
    await pricingSubmitRequest({
      variables: {
        input: {
          email: values.email,
          name: values.name,
          phone: values.mobilephone,
          companyName: values.companyName,
        },
      },
      onCompleted: () => {
        setModalDarkTypes("success")
      },
      onError: error => {
        message.error(error.message)
        // eslint-disable-next-line no-console
        console.log(error)
      },
    })
  }

  return (
    <Row justify={"center"}>
      <Col span={20} xxl={20} xl={20} lg={20} md={24} sm={24} xs={24}>
        <Title level={3} centered className={"form-section-title"}>
          <span className={"bold"}>{"Pricing "}</span>
          <span className={"w300"}>{"Request "}</span>
        </Title>

        <Paragraph color={"website-secondary"} centered style={{ paddingTop: 24 }}>
          {"Please complete this form to receive pricing options and information"}
        </Paragraph>

        <div className={"modal-scrollable-container"}>
          <Form<PricingFormValues> form={form} onFinish={onFinish} autoComplete={"off"}>
            <Row gutter={[0, 24]} style={{ paddingTop: 40 }}>
              <Col span={24}>
                <Form.Item
                  className={"website-form-item"}
                  name={"email"}
                  rules={[
                    { type: "email", message: "Please enter a valid email" },
                    { required: true, message: "Email is required" },
                  ]}
                >
                  <Input placeholder={"Email *"} className={"website-input"} />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  className={"website-form-item"}
                  name={"name"}
                  rules={[{ required: true, message: "Please enter your Name" }]}
                >
                  <Input placeholder={"First Name *"} className={"website-input"} />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  className={"website-form-item"}
                  name={"companyName"}
                  rules={[{ required: true, message: "Please enter your Company name" }]}
                >
                  <Input placeholder={"Company Name *"} className={"website-input"} />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  className={"website-form-item"}
                  name={"mobilephone"}
                  rules={[
                    {
                      pattern: /^\+27 \(\d{2}\) \d{3}-\d{4}$/,
                      message: "Please enter a valid phone number",
                    },
                  ]}
                >
                  <IMaskInput
                    className={"ant-input website-input"}
                    mask={"+27 (00) 000-0000"}
                    placeholder={"+27 (00) 000-0000"}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row justify={"start"} style={{ paddingTop: 32 }}>
              <Col>
                <Paragraph color={"website-secondary"} style={{ paddingBottom: 8 }}>
                  {
                    "Easetech is committed to protecting and respecting your privacy, and we’ll only use your personal information to administer your account and to provide the products and services you requested from us. From time to time, we would like to contact you about our products and services, as well as other content that may be of interest to you. If you consent to us contacting you for this purpose, please tick below to say how you would like us to contact you:"
                  }
                </Paragraph>
              </Col>

              <Col>
                <Form.Item
                  name={"consent"}
                  valuePropName={"checked"}
                  required
                  rules={[
                    {
                      validator: (_, value) =>
                        value ? Promise.resolve() : Promise.reject(new Error("This field is required")),
                    },
                  ]}
                >
                  <Checkbox defaultChecked={false} onChange={handleCheckboxChange}>
                    <Text>{"I agree to receive other communications from Easetech.*"}</Text>
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col>
                <Paragraph color={"website-secondary"} style={{ paddingBottom: 8 }}>
                  {
                    "You can unsubscribe from these communications at any time. For more information on how to unsubscribe, our privacy practices, and how we are committed to protecting and respecting your privacy, please review our "
                  }
                  <Link to={website.privacyPolicy} style={{ color: "inherit", textDecoration: "underline" }}>
                    Privacy Policy
                  </Link>
                </Paragraph>
                <Paragraph color={"website-secondary"}>
                  {
                    "By clicking submit below, you consent to allow Easetech to store and process the personal information submitted above to provide you the content requested."
                  }
                </Paragraph>
              </Col>
            </Row>
          </Form>

          <Row justify={"center"} style={{ paddingTop: 32 }}>
            <Button text={"SEND REQUEST"} loading={loading} disabled={isSubmitDisabled} onClick={handleSubmitClick} />
          </Row>
        </div>
      </Col>
    </Row>
  )
}

export default PricingRequest
